/**
 * advisorConsentAccess — FE-Spiegel der Server-Konstanten für den §3-Mandanten-
 * Consent (B3) und die §5-Kanzlei-Onboarding-Bestätigung (B4).
 *
 * Single source of truth für die FE-Seite des Consent-/Onboarding-Zwangs
 * (architecture-stb-onboarding-consent.md §4/§9). Die Versions- und Pfad-
 * Konstanten MÜSSEN exakt mit den Server-Konstanten in
 * `functions/src/access/advisorConsent.ts` (gemergt) und der clientInvite-AVV-
 * Version übereinstimmen — driften sie, antwortet der Server mit
 * `consent_missing`/`onboarding_stale`.
 *
 * Fail-safe (Inertheit): Solange die vom Operator/J-3 anzulegenden Legal-Dateien
 * (`public/legal/advisor-consent-v1.md` / `kanzlei-onboarding-v1.md`) fehlen oder
 * den {@link LEGAL_DRAFT_MARKER} tragen, sperren die Dialoge fail-safe und der
 * neue Zwang bleibt inert. Diese Datei LIEST die Dateien nur (per Laufzeit-Fetch)
 * — sie legt sie NIEMALS an (Operator-Schritt M-OB-15).
 *
 * @module advisorConsentAccess
 */

/**
 * Aktuelle §3-Consent-Version, die der Server akzeptiert (Gegenstück zu
 * `ADVISOR_CONSENT_VERSION` in `functions/src/access/advisorConsent.ts`). Zugleich
 * der Dateiname-Stamm des §3-Dokuments. Muss byte-gleich zum Server sein.
 */
export const ADVISOR_CONSENT_VERSION = 'advisor-consent-v1-2026-07';

/**
 * Dateistamm des §3-Consent-Dokuments unter `public/legal/`. Bewusst OHNE das
 * Versions-Datumssuffix von {@link ADVISOR_CONSENT_VERSION}: die vom Operator (J-3,
 * M-OB-15) angelegte und in `legal-approvals.json` freigegebene Datei heißt
 * `advisor-consent-v1.md` (so auch dokumentiert im Server-Kommentar von
 * `functions/src/access/advisorConsent.ts`). Die Version wird separat als
 * Consent-Payload an den Server gesendet — sie ist NICHT Teil des Dateipfads.
 */
export const ADVISOR_CONSENT_DOC_STEM = 'advisor-consent-v1';

/**
 * Öffentlicher Pfad des §3-Consent-Dokuments (statisch ausgeliefert). Die Datei legt
 * der Operator an (J-3, M-OB-15) — der FE-Code liest sie nur. Fehlt sie, lädt der
 * Fetch 404 → fail-closed (Dialog sperrt).
 */
export const ADVISOR_CONSENT_DOC_PATH = `/legal/${ADVISOR_CONSENT_DOC_STEM}.md`;

/**
 * Aktuelle §5-Kanzlei-Onboarding-Version, die der Server akzeptiert (Gegenstück zu
 * `KANZLEI_ONBOARDING_VERSION` in `functions/src/access/advisorConsent.ts`).
 * Zugleich der Dateiname-Stamm des §5-Dokuments. Muss byte-gleich zum Server sein.
 */
export const KANZLEI_ONBOARDING_VERSION = 'kanzlei-onboarding-v1-2026-07';

/**
 * Dateistamm des §5-Kanzlei-Onboarding-Dokuments unter `public/legal/`. Analog zu
 * {@link ADVISOR_CONSENT_DOC_STEM} ohne Versions-Datumssuffix — die freigegebene
 * Operator-Datei heißt `kanzlei-onboarding-v1.md`.
 */
export const KANZLEI_ONBOARDING_DOC_STEM = 'kanzlei-onboarding-v1';

/**
 * Öffentlicher Pfad des §5-Kanzlei-Onboarding-Dokuments (statisch ausgeliefert).
 * Operator-Datei (J-3, M-OB-15); der FE-Code liest sie nur.
 */
export const KANZLEI_ONBOARDING_DOC_PATH = `/legal/${KANZLEI_ONBOARDING_DOC_STEM}.md`;

/**
 * FE-Spiegel der clientInvite-AVV-Version (Gegenstück zu
 * `CLIENT_INVITE_AVV_VERSION` in `functions/src/tenant/clientInviteFunctions.ts`).
 * Wird im §5-Text für den `{{avv_version}}`-Platzhalter interpoliert (§6.1). Der in
 * Bezug genommene AVV-Vertragstext selbst ist H1-HUMAN-LAWYER-gated und existiert
 * bis dahin nicht — die Interpolation bleibt bis dahin ein (inertes) Platzhalter-
 * Label.
 */
export const CLIENT_INVITE_AVV_VERSION = 'stb-avv-v1-2026-07';

/**
 * Dateistamm des Kanzlei-AVV-Vertragstexts unter `public/legal/`. Analog zu
 * {@link ADVISOR_CONSENT_DOC_STEM}/{@link KANZLEI_ONBOARDING_DOC_STEM} OHNE das
 * Versions-Datumssuffix von {@link CLIENT_INVITE_AVV_VERSION}: die vom Operator (J-3)
 * angelegte und in `legal-approvals.json` freigegebene Datei heißt `stb-avv-v1.md`.
 * Der Stamm MUSS zur Version `stb-avv-v1-2026-07` passen (Datei `stb-avv-v1.md`).
 */
export const STB_AVV_DOC_STEM = 'stb-avv-v1';

/**
 * Öffentlicher Pfad des Kanzlei-AVV-Vertragstexts (Art. 28 DSGVO, statisch
 * ausgeliefert). Wird im {@link KanzleiOnboardingDialog} per Laufzeit-Fetch geladen
 * und VOR dem Setzen der Onboarding-/AVV-Zustimmung (`avvAccepted`) einsehbar
 * gemacht (jurist-R1). Fehlt die Datei oder trägt sie den {@link LEGAL_DRAFT_MARKER},
 * lädt der Fetch fail-closed (`missing`/`draft`) → der Aktivieren-Haken bleibt
 * gesperrt (Inertheit, analog zum §5-Onboarding-Dokument).
 */
export const STB_AVV_DOC_PATH = `/legal/${STB_AVV_DOC_STEM}.md`;

/**
 * Marker, an dem der Entwurfs-Zustand eines Legal-Dokuments erkannt wird
 * (identisch zum Inbound-Draft-Marker, `config/inboundAccess.ts:INBOUND_AVV_DRAFT_MARKER`).
 * Solange eine Legal-Datei diesen Marker enthält (Operator setzt ihn bis zur
 * Jurist-Freigabe), bleibt der zugehörige Dialog gesperrt (Fail-safe).
 */
export const LEGAL_DRAFT_MARKER = 'ENTWURF';
